import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  Lock, Unlock, RefreshCw, FileSpreadsheet, Briefcase, Package, Loader2, AlertTriangle,
} from 'lucide-react';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');

export default function PagosBloqueadosPage() {
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canLiberar = ['admin', 'administrador', 'gerencia'].includes(role);

  const [data, setData] = useState({ items: [], total: 0, monto_total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [liberarDlg, setLiberarDlg] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [liberando, setLiberando] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flujo-caja/cxp/facturas-bloqueadas');
      setData(res.data || { items: [], total: 0, monto_total: 0 });
    } catch { toast.error('Error al cargar facturas bloqueadas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const items = (data.items || []).filter((f) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return [f.folio, f.nombre_proveedor, f.rut_proveedor].some((v) => (v || '').toLowerCase().includes(t));
  });

  const liberar = async () => {
    if ((motivo || '').trim().length < 5) { toast.error('Motivo mínimo 5 caracteres'); return; }
    setLiberando(true);
    try {
      const res = await api.post(`/flujo-caja/cxp/factura/${liberarDlg.alegra_id}/liberar-pago`, {
        motivo: motivo.trim(),
      });
      toast.success(res.data?.message || 'Pago liberado');
      setLiberarDlg(null); setMotivo('');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al liberar');
    } finally { setLiberando(false); }
  };

  const exportarExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet((data.items || []).map((f) => ({
        Folio: f.folio,
        RUT: f.rut_proveedor,
        Proveedor: f.nombre_proveedor,
        Monto: f.monto,
        Vencimiento: f.fecha_vencimiento,
        Tipo: f.tipo_factura,
        Anticipo: f.es_anticipo ? 'Sí' : 'No',
        'Bloqueada por': f.bloqueada_por,
        'Fecha bloqueo': f.bloqueada_fecha,
        Motivo: f.motivo_bloqueo,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bloqueadas');
      XLSX.writeFile(wb, `pagos_bloqueados_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lock className="w-6 h-6 text-red-600" /> Pagos Bloqueados
          </h1>
          <p className="text-sm text-muted-foreground">
            Facturas con bloqueo activo en el archivo bancario. Total: <strong>{data.total}</strong> ·
            Monto retenido: <strong>${fmtCLP(data.monto_total)}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
          <Button variant="outline" size="sm" onClick={exportarExcel}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Buscar</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Folio, proveedor, RUT…"
              className="h-9 mt-1" data-testid="input-search-bloq" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4"><TableSkeleton rows={6} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              🎉 No hay facturas bloqueadas para pago.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-pagos-bloqueados">
                <thead className="bg-muted text-xs text-left">
                  <tr>
                    <th className="px-3 py-2">Folio</th>
                    <th className="px-3 py-2">Proveedor</th>
                    <th className="px-3 py-2 text-right">Monto</th>
                    <th className="px-3 py-2">Vence</th>
                    <th className="px-3 py-2">Motivo</th>
                    <th className="px-3 py-2">Tipo</th>
                    {canLiberar && <th className="px-3 py-2 text-center">Acción</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => (
                    <tr key={f.alegra_id} className="border-t hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-mono text-xs">{f.folio}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{f.nombre_proveedor}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{f.rut_proveedor}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">${fmtCLP(Math.round(f.monto || 0))}</td>
                      <td className="px-3 py-2 text-xs">{f.fecha_vencimiento || '—'}</td>
                      <td className="px-3 py-2 text-[11px] text-red-700">
                        {f.motivo_bloqueo || '—'}
                        <div className="text-[10px] text-muted-foreground">
                          {f.bloqueada_por}{f.bloqueada_fecha ? ` · ${f.bloqueada_fecha.slice(0, 10)}` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {f.tipo_factura === 'servicio' ? (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5">
                            <Briefcase className="w-3 h-3" /> Servicio
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
                            <Package className="w-3 h-3" /> Material
                          </span>
                        )}
                        {f.es_anticipo && (
                          <span className="ml-1 inline-flex text-[10px] bg-orange-50 text-orange-700 border border-orange-200 rounded px-1.5 py-0.5">Anticipo</span>
                        )}
                      </td>
                      {canLiberar && (
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => { setLiberarDlg(f); setMotivo(''); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded transition-colors"
                            title="Liberar pago (override gerencia)"
                            data-testid={`btn-liberar-${f.alegra_id}`}>
                            <Unlock className="w-3.5 h-3.5" /> Liberar
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {!canLiberar && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Solo admin / gerencia pueden liberar pagos bloqueados.
        </div>
      )}

      <Dialog open={!!liberarDlg} onOpenChange={(o) => { if (!o) setLiberarDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-liberar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Unlock className="w-5 h-5" /> Liberar Pago (override Gerencia)
            </DialogTitle>
            <DialogDescription>
              Vas a liberar la factura <strong>{liberarDlg?.folio}</strong> de <strong>{liberarDlg?.nombre_proveedor}</strong>
              ({liberarDlg ? `$${fmtCLP(Math.round(liberarDlg.monto || 0))}` : ''}).
              Quedará incluida en el próximo archivo bancario sin necesidad de VB de lote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              ⚠ Esta acción queda registrada en auditoría con tu usuario y motivo. Reservar para casos urgentes.
            </div>
            <div>
              <Label htmlFor="motivo-lib" className="text-xs font-medium">Motivo (mín. 5 caracteres)</Label>
              <Textarea id="motivo-lib" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: pago urgente arriendo, electricidad sin VB todavía pero válida…"
                className="mt-1 text-sm" data-testid="input-motivo-lib" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLiberarDlg(null)} disabled={liberando}>Cancelar</Button>
            <Button onClick={liberar} disabled={liberando || motivo.trim().length < 5}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-confirm-liberar">
              {liberando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Unlock className="w-4 h-4 mr-1" />}
              Liberar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
